import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  Container,
  IconButton,
  Avatar,
  LinearProgress,
  Paper,
  Fade,
  Chip,
  CircularProgress,
  Link as MuiLink,
  FormControlLabel,
  Switch,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  OutlinedInput
} from '@mui/material';
import { CreateOpinionRequest } from '../../interface/Opinion';
import { createOpinion } from '../../services/OpinionService';
import { getUrlPreview, UrlPreviewData } from '../../services/UrlPreviewService';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import SendIcon from '@mui/icons-material/Send';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LinkIcon from '@mui/icons-material/Link';
import CloseIcon from '@mui/icons-material/Close';
import MainLayout from '../layout/MainLayout';

interface CreateOpinionProps {
  onSuccess?: () => void;
}

const CreateOpinion: React.FC<CreateOpinionProps> = ({ onSuccess }) => {
  const { user } = useAuth();
  const currentUserId = user?._id || '';
  const currentUsername = user?.username || '';
  const [content, setContent] = useState('');
  const [isPublic, setIsPublic] = useState(false);
  const [category, setCategory] = useState('General');
  const [customCategory, setCustomCategory] = useState('');
  const [targetAudience, setTargetAudience] = useState<string[]>([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [urlPreview, setUrlPreview] = useState<UrlPreviewData | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [peerQuestions, setPeerQuestions] = useState<string[]>([]);
  const previewTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const navigate = useNavigate();

  const categories = ['General', 'Finance', 'Software', 'Business', 'Entrepreneur', 'Politics', 'Social Welfare', 'Technology', 'Education', 'Healthcare', 'Custom'];
  const audienceOptions = ['Finance', 'Software', 'Business', 'Entrepreneur', 'Politics', 'Social Welfare', 'Technology', 'Education', 'Healthcare', 'General'];

  // URL detection regex
  const urlRegex = /(https?:\/\/[^\s]+)/g;

  // Extract URL from content
  const extractUrl = (text: string): string | null => {
    const matches = text.match(urlRegex);
    return matches && matches.length > 0 ? matches[0] : null;
  };

  // Fetch URL preview
  useEffect(() => {
    // Clear previous timeout
    if (previewTimeoutRef.current) {
      clearTimeout(previewTimeoutRef.current);
    }

    const url = extractUrl(content);
    
    if (!url) {
      setUrlPreview(null);
      return;
    }

    // Debounce the API call
    previewTimeoutRef.current = setTimeout(async () => {
      try {
        setLoadingPreview(true);
        const preview = await getUrlPreview(url);
        setUrlPreview(preview);
      } catch (error) {
        console.error('Failed to fetch URL preview:', error);
        setUrlPreview(null);
      } finally {
        setLoadingPreview(false);
      }
    }, 1000); // Wait 1 second after user stops typing

    return () => {
      if (previewTimeoutRef.current) {
        clearTimeout(previewTimeoutRef.current);
      }
    };
  }, [content]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!content.trim()) {
      setError('Please enter your opinion');
      return;
    }

    if (content.length > 1000) {
      setError('Opinion must be 1000 characters or less');
      return;
    }

    try {
      setLoading(true);
      const finalCategory = category === 'Custom' ? customCategory.trim() : category;
      
      const opinionData: CreateOpinionRequest = {
        content: content.trim(),
        userId: currentUserId,
        username: currentUsername,
        isPublic: isPublic,
        category: finalCategory || 'General',
        targetAudience: targetAudience.length > 0 ? targetAudience : [],
        questions: peerQuestions
          .map((q) => q.trim())
          .filter((q) => q.length > 0)
      };

      const createdOpinion = await createOpinion(opinionData);
      setContent('');
      setUrlPreview(null);
      
      if (onSuccess) {
        onSuccess();
      } else {
        navigate(`/opinions/${createdOpinion._id}`);
      }
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to create opinion');
    } finally {
      setLoading(false);
    }
  };

  const maxCharacters = 1000;
  const characterCount = content.length;
  const characterPercentage = (characterCount / maxCharacters) * 100;
  const isNearLimit = characterCount > maxCharacters * 0.85;

  return (
    <MainLayout title="Create Opinion">
      <Container maxWidth="md" sx={{ py: { xs: 2, md: 3 }, px: { xs: 1.5, sm: 2, md: 3 } }}>
        <Fade in timeout={800}>
          <Card>
            <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
              {/* User Info */}
              <Box display="flex" alignItems="center" mb={2.5}>
                <Avatar
                  sx={{
                    width: 44,
                    height: 44,
                    bgcolor: 'primary.main',
                    mr: 1.5,
                    fontSize: '1.125rem'
                  }}
                >
                  {currentUsername.charAt(0).toUpperCase()}
                </Avatar>
                <Box>
                  <Typography variant="body1" fontWeight={500} sx={{ fontSize: '0.9375rem' }}>
                    {currentUsername}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                    Share your perspective
                  </Typography>
                </Box>
              </Box>

              {error && (
                <Alert
                  severity="error"
                  sx={{
                    mb: 3,
                    borderRadius: 2,
                    animation: 'slideIn 0.3s ease-out'
                  }}
                >
                  {error}
                </Alert>
              )}

              <form onSubmit={handleSubmit}>
                <TextField
                  fullWidth
                  multiline
                  minRows={8}
                  maxRows={16}
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  placeholder="What's on your mind? Share your thoughts in detail..."
                  variant="outlined"
                  inputProps={{ maxLength: 1000 }}
                  disabled={loading}
                  sx={{ 
                    mb: 2,
                    '& .MuiOutlinedInput-root': {
                      fontSize: '1rem'
                    }
                  }}
                />

                {/* Character Counter with Progress */}
                <Box sx={{ mb: 2.5 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.75}>
                    <Typography
                      variant="body2"
                      color={isNearLimit ? 'error.main' : 'text.secondary'}
                      fontWeight={isNearLimit ? 500 : 400}
                      sx={{ fontSize: '0.875rem' }}
                    >
                      {characterCount} / {maxCharacters}
                    </Typography>
                    {characterCount > 0 && (
                      <Typography
                        variant="body2"
                        color={characterCount >= 300 ? 'error.main' : 'success.main'}
                        fontWeight={500}
                        sx={{ fontSize: '0.875rem' }}
                      >
                        {maxCharacters - characterCount} remaining
                      </Typography>
                    )}
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={characterPercentage}
                    sx={{
                      height: 4,
                      borderRadius: 2,
                      '& .MuiLinearProgress-bar': {
                        bgcolor: isNearLimit ? 'error.main' : 'primary.main',
                        borderRadius: 2
                      }
                    }}
                  />
                </Box>

                {/* Category Selection */}
                <Box sx={{ mb: 2.5 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Category</InputLabel>
                    <Select
                      value={category}
                      onChange={(e) => setCategory(e.target.value)}
                      label="Category"
                      disabled={loading}
                    >
                      {categories.map((cat) => (
                        <MenuItem key={cat} value={cat}>
                          {cat}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {category === 'Custom' && (
                    <TextField
                      fullWidth
                      size="small"
                      value={customCategory}
                      onChange={(e) => setCustomCategory(e.target.value)}
                      placeholder="Enter custom category"
                      disabled={loading}
                      sx={{ mt: 1.5 }}
                    />
                  )}
                </Box>

                {/* Target Audience Selection */}
                <Box sx={{ mb: 2.5 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Seeking opinions from (like-minded people)</InputLabel>
                    <Select
                      multiple
                      value={targetAudience}
                      onChange={(e) => setTargetAudience(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                      input={<OutlinedInput label="Seeking opinions from (like-minded people)" />}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((value) => (
                            <Chip key={value} label={value} size="small" />
                          ))}
                        </Box>
                      )}
                      disabled={loading}
                    >
                      {audienceOptions.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: 'block', fontSize: '0.8125rem' }}>
                    Select topics to get opinions from like-minded people interested in these areas
                  </Typography>
                </Box>

                {/* Interactive Peer Questions */}
                <Box sx={{ mb: 3, mt: 1 }}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      borderStyle: 'dashed',
                      borderColor: 'primary.light',
                      background: 'linear-gradient(135deg, rgba(102,126,234,0.04), rgba(118,75,162,0.04))'
                    }}
                  >
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5} flexWrap="wrap" gap={1}>
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          Add interactive prompts (optional)
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Ask up to 5 quick questions to help peers respond with more focused feedback.
                        </Typography>
                      </Box>
                      <Chip
                        label={`${peerQuestions.length}/5`}
                        size="small"
                        sx={{ fontWeight: 500 }}
                      />
                    </Box>

                    {peerQuestions.map((q, index) => (
                      <Box
                        key={index}
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 1.5,
                          mb: 1.5
                        }}
                      >
                        <Chip
                          label={`Q${index + 1}`}
                          size="small"
                          color="primary"
                          sx={{ mt: 0.75, fontWeight: 600 }}
                        />
                        <TextField
                          fullWidth
                          size="small"
                          multiline
                          minRows={2}
                          maxRows={4}
                          value={q}
                          onChange={(e) => {
                            const next = [...peerQuestions];
                            next[index] = e.target.value;
                            setPeerQuestions(next);
                          }}
                          placeholder="Example: What would you improve in this idea? What risks do you see?"
                          disabled={loading}
                        />
                        <IconButton
                          size="small"
                          onClick={() => {
                            const next = peerQuestions.filter((_, i) => i !== index);
                            setPeerQuestions(next);
                          }}
                          sx={{ mt: 0.75 }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}

                    {peerQuestions.length < 5 && (
                      <Box mt={peerQuestions.length ? 0.5 : 1}>
                        <Button
                          variant="text"
                          size="small"
                          onClick={() => setPeerQuestions([...peerQuestions, ''])}
                          disabled={loading}
                        >
                          + Add a peer question
                        </Button>
                      </Box>
                    )}
                  </Paper>
                </Box>

                {/* Public Toggle */}
                <Box sx={{ mb: 2.5 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={isPublic}
                        onChange={(e) => setIsPublic(e.target.checked)}
                        disabled={loading}
                        color="primary"
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.9375rem' }}>
                          Make this opinion public
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.8125rem' }}>
                          Anyone can view this opinion without logging in
                        </Typography>
                      </Box>
                    }
                    sx={{ alignItems: 'flex-start' }}
                  />
                </Box>

                {/* URL Preview */}
                {(urlPreview || loadingPreview) && (
                  <Box sx={{ mb: 2 }}>
                    {loadingPreview ? (
                      <Card sx={{ p: 2, bgcolor: 'grey.50' }}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <CircularProgress size={20} />
                          <Typography variant="body2" color="text.secondary">
                            Loading preview...
                          </Typography>
                        </Box>
                      </Card>
                    ) : urlPreview ? (
                      <Card
                        sx={{
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 2,
                          overflow: 'hidden',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            boxShadow: 2
                          }
                        }}
                      >
                        <Box display="flex" position="relative">
                          {urlPreview.image && (
                            <Box
                              component="img"
                              src={urlPreview.image}
                              alt={urlPreview.title || 'Preview'}
                              sx={{
                                width: 120,
                                height: 120,
                                objectFit: 'cover',
                                borderRadius: 2,
                                display: { xs: 'none', sm: 'block' }
                              }}
                            />
                          )}
                          <Box flex={1} p={1.5}>
                            <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={0.5}>
                              <Box display="flex" alignItems="center" gap={0.5} flex={1}>
                                <LinkIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                  }}
                                >
                                  {urlPreview.siteName || new URL(urlPreview.url).hostname}
                                </Typography>
                              </Box>
                              <IconButton
                                size="small"
                                onClick={() => setUrlPreview(null)}
                                sx={{ p: 0.5 }}
                              >
                                <CloseIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Box>
                            {urlPreview.title && (
                              <Typography
                                variant="body2"
                                fontWeight={500}
                                sx={{
                                  mb: 0.5,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical'
                                }}
                              >
                                {urlPreview.title}
                              </Typography>
                            )}
                            {urlPreview.description && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical',
                                  mb: 0.5
                                }}
                              >
                                {urlPreview.description}
                              </Typography>
                            )}
                            <MuiLink
                              href={urlPreview.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              variant="caption"
                              sx={{
                                color: 'primary.main',
                                textDecoration: 'none',
                                '&:hover': {
                                  textDecoration: 'underline'
                                }
                              }}
                            >
                              {urlPreview.url}
                            </MuiLink>
                          </Box>
                        </Box>
                      </Card>
                    ) : null}
                  </Box>
                )}

                {/* Action Buttons */}
                <Box display="flex" justifyContent="flex-end" alignItems="center" gap={{ xs: 1, sm: 1.5 }} flexWrap="wrap">
                  <Button
                    variant="outlined"
                    onClick={() => navigate('/home')}
                    disabled={loading}
                    size="small"
                    sx={{
                      minWidth: { xs: 80, sm: 100 },
                      minHeight: { xs: 36, sm: 40 },
                      fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                      touchAction: 'manipulation'
                    }}
                  >
                    Cancel
                  </Button>

                  <Button
                    type="submit"
                    variant="contained"
                    disabled={loading || !content.trim()}
                    startIcon={loading ? <CircularProgress size={16} /> : <SendIcon sx={{ fontSize: { xs: 16, sm: 18 } }} />}
                    size="small"
                    sx={{
                      minWidth: { xs: 120, sm: 140 },
                      minHeight: { xs: 36, sm: 40 },
                      fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                      touchAction: 'manipulation'
                    }}
                  >
                    {loading ? 'Posting...' : 'Post Opinion'}
                  </Button>
                </Box>
              </form>
            </CardContent>
          </Card>
        </Fade>
      </Container>
    </MainLayout>
  );
};

export default CreateOpinion;
